<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Configurações gerais</p>
        </div>

        <div class="muven-content">
            <Breadcrumb :items="breadcrumbList" class="mb-3" v-if="!loading"/>

            <Loading page v-if="loading" />

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="canal"
                :identifier="$route.params.id"
                list-path="/canais"
                list-label="Voltar para canais"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <ConfirmByTypingModalCmp
                ref="confirmAllowExportAll"
                :modal-id="ID_CONFIRM_ALLOW_EXPORT_ALL_MODAL"
                title="Aviso de cautela"
                confirm-label="Gravar mesmo assim"
                confirm-hint="Nada é gravado enquanto você não confirmar."
                @confirm="allowExportAllConfirmedHandler()"
                @cancel="allowExportAllCancelledHandler()">
                <p>
                    Opção selecionada: <strong>Exportar tudo</strong>. Ao ativar esta configuração, os dados
                    eventualmente já registrados no canal <strong>{{ channelSettingsForm.name }}</strong> podem
                    ser sobrescritos pelos dados do Muven.
                </p>

                <p class="confirm-allow-export-label">O que pode ser sobrescrito</p>

                <p class="confirm-allow-export-chips">
                    <span v-for="item in overwrittenByAllowExportAll" :key="item">{{ item }}</span>
                </p>

                <p class="confirm-allow-export-danger">
                    Esta operação é irreversível e pode impactar significativamente a integridade dos dados
                    no canal. Se a configuração anterior era diferente desta, prossiga com cautela.
                </p>
            </ConfirmByTypingModalCmp>

            <div class="modal" tabindex="-1" role="dialog" id="muven-price-management-store-modified-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Loja responsável pelo catálogo</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div>
                                <p>A loja responsável pelo catálogo foi alterada e com isso os produtos vinculados <span v-show="!!initialPriceManagementStore">à loja <b>{{initialPriceManagementStore.name}}</b></span> no canal <b>{{channelSettingsForm.name}}</b> passarão a pertencer à loja <b>{{namePriceManagementStore}}</b>.</p> 
                                <p>Este tipo de modificação deve ser feito em último caso, pois é um processo lento e arriscado.</p>
                                <p>Deseja realmente alterar a loja responsável pelo catálogo?</p>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button"
                                class="btn btn-primary"
                                @click="saveSettings(channelSettingsForm)">Sim, alterar loja e continuar</button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeModalPriceManagementStoreModifiedModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="modal" tabindex="-1" role="dialog" id="muven-entity-template-explanation-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Variáveis e explicações sobre as informações do produto</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <ul class="nav nav-tabs" id="channel-settings-form-tab">
                                    <li class="nav-item">
                                        <a class="nav-link active" data-toggle="tab" href="#channel-settings-entity-template-available-variables">Variáveis disponíveis</a>
                                    </li>

                                    <li class="nav-item">
                                        <a class="nav-link" data-toggle="tab" href="#channel-settings-entity-template-how-to-use">Exemplos de uso</a>
                                    </li>
                                </ul>

                                <div class="tab-content">
                                    <div class="tab-pane fade show active" id="channel-settings-entity-template-available-variables">
                                        <div class="row">
                                            <div class="col-12" v-for="(productEntityTemplate, index) in productEntityTemplateFieldOptions" :key="index">
                                                <div class="muven-tip tip-info mb-1">
                                                    <p class="entity-template-variable-item">
                                                        <b>{{productEntityTemplate.name}}</b>: <span>{{productEntityTemplate.description}}</span>
                                                        <fa :icon='["far", "copy"]' class="icon-copy" :data-clipboard-text="productEntityTemplate.name"/>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="tab-pane fade" id="channel-settings-entity-template-how-to-use">
                                        <div class="row">
                                            <div class="col-12 mb-3">
                                                <div class="muven-tip tip-info">
                                                    <p>Supondo que temos um produto com as seguintes informações a serem enviadas para o canal</p>
                                                    <ul class="pl-3 mt-3 mb-0">
                                                        <li><b>Nome</b>: TÊNIS CORRIDA</li>
                                                        <li><b>Marca</b>: NIKE</li>
                                                        <li><b>Preço de venda</b>: R$ 199,99</li>
                                                        <li><b>Descrição principal</b>: Tênis confortável e leve para corrida em asfalto</li>
                                                        <li><b>Altura</b>: 14</li>
                                                        <li><b>Largura</b>: 8</li>
                                                        <li><b>Profundidade</b>: 40</li>
                                                        <li><b>Peso</b>: 250</li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div class="col-12 form-group">
                                                <label>Nome</label>
                                                <input class="form-control" readonly value="{nome_produto} da marca {nome_marca} e valor R$ {preco_produto}" />
                                                <small class="text-muted">Será enviado como: TÊNIS CORRIDA da marca NIKE e valor R$ 199,99</small>
                                            </div>

                                            <div class="col-12 form-group">
                                                <label>Descrição</label>
                                                <textarea class="form-control" readonly>{descricao_produto} e altura {altura_produto}cm, largura {largura_produto}, profundidade {profundidade_produto} e peso {peso_produto} gramas</textarea>
                                                <small class="text-muted">Será enviado como: Tênis confortável e leve para corrida em asfalto e altura 10cm, largura 8cm, profundidade 40cm e peso 250 gramas</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeModalEntityTemplateExplanation()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="channel-general" v-if="!loading && !loadError">
                <ChannelPageHeaderCmp :channel="channel" eyebrow="Configurações gerais">
                    <div class="cg-activation" :class="{ 'is-on': channelSettingsForm.isActive }">
                        <div class="cg-activation-text">
                            <p class="cg-activation-title">
                                <span class="cg-activation-dot"></span>
                                Integração {{channelSettingsForm.isActive ? 'ativa' : 'inativa'}}
                            </p>
                            <p class="cg-activation-hint">{{activationHint}}</p>
                        </div>
                        <SwitchToggle
                            class="cg-activation-switch"
                            :prop-check="channelSettingsForm.isActive"
                            :label-checked="''"
                            :label-unchecked="''"
                            @updated="channelIsActiveModifiedHandler" />
                    </div>
                </ChannelPageHeaderCmp>

                <div class="cg-layout">
                    <nav class="cg-index" aria-label="Seções da página">
                        <a v-for="section in sections" :key="section.id"
                            :href="`#${section.id}`"
                            class="cg-index-link"
                            :class="{ 'is-current': currentSection === section.id }"
                            @click.prevent="scrollToSection(section.id)">
                            {{section.label}}
                        </a>
                    </nav>

                    <div class="cg-sections" :aria-busy="savingContent">
                        <section id="cg-identificacao" class="cg-section">
                            <header class="cg-section-header">
                                <h2 class="cg-section-title">Identificação</h2>
                                <p class="cg-section-description">Como este canal aparece para você dentro do Muven.</p>
                            </header>

                            <div class="cg-field">
                                <label for="cg-name">Nome no Muven</label>
                                <input id="cg-name" type="text" class="form-control" v-model="channelSettingsForm.name" />
                                <small class="cg-help">Aparece nas listas e relatórios. O nome real continua sendo {{channel.getRealName()}}.</small>
                            </div>
                        </section>

                        <section id="cg-estoque-preco" class="cg-section">
                            <header class="cg-section-header">
                                <h2 class="cg-section-title">Estoque e preço</h2>
                                <p class="cg-section-description">Quanto do seu estoque e qual preço o {{channel.getRealName()}} recebe.</p>
                            </header>

                            <div class="cg-split">
                                <div class="cg-field">
                                    <label for="cg-stock">Disponibilidade de estoque</label>
                                    <div class="cg-input-suffix">
                                        <input id="cg-stock" type="number" class="form-control" min="1" max="100" step="0.1"
                                            v-model="channelSettingsForm.availableStockPercentage">
                                        <span class="cg-suffix">%</span>
                                    </div>
                                    <small class="cg-help">100% envia todo o estoque da loja; 50% envia a metade.</small>
                                </div>

                                <div class="cg-preview" aria-live="polite">
                                    <span class="cg-preview-label">Exemplo</span>
                                    <div class="cg-preview-flow">
                                        <span class="cg-preview-value">
                                            <small>Na loja</small>
                                            <strong>100 un</strong>
                                        </span>
                                        <fa icon="arrow-right" class="cg-preview-arrow" />
                                        <span class="cg-preview-value">
                                            <small>No {{channel.getRealName()}}</small>
                                            <strong>{{stockPreview}} un</strong>
                                        </span>
                                    </div>
                                    <span class="cg-meter"><span class="cg-meter-fill" :style="{ width: `${stockPreview}%` }"></span></span>
                                </div>
                            </div>

                            <hr class="cg-divider" />

                            <div class="cg-split">
                                <div class="cg-field-pair">
                                    <div class="cg-field">
                                        <label for="cg-price">Variação de preço</label>
                                        <div class="cg-input-suffix">
                                            <input id="cg-price" type="number" class="form-control" min="1" step="1"
                                                v-model="channelSettingsForm.pricePercentage">
                                            <span class="cg-suffix">%</span>
                                        </div>
                                    </div>

                                    <div class="cg-field">
                                        <label>Centavos</label>
                                        <SelectWrapperCmp
                                            :show-remove-button="!!channelSettingsForm.priceDecimalsPreferenceString"
                                            @removed="handlePriceDecimalsPreferenceRemoved">
                                                <template v-slot:select>
                                                    <multiselect
                                                        v-model="channelSettingsForm.priceDecimalsPreferenceString"
                                                        :options="priceDecimals"
                                                        :multiple="false"
                                                        :show-labels="false"
                                                        placeholder="Manter" />
                                                </template>
                                        </SelectWrapperCmp>
                                    </div>

                                    <small class="cg-help cg-field-pair-help">100% mantém o preço da loja; 200% dobra. Os centavos fixam o final do preço (ex.: ,90).</small>
                                </div>

                                <div class="cg-preview" aria-live="polite">
                                    <span class="cg-preview-label">
                                        Exemplo
                                        <span class="cg-delta">{{priceDeltaLabel}}</span>
                                    </span>
                                    <div class="cg-preview-flow">
                                        <span class="cg-preview-value">
                                            <small>Na loja</small>
                                            <strong>R$ {{initialPrice}},00</strong>
                                        </span>
                                        <fa icon="arrow-right" class="cg-preview-arrow" />
                                        <span class="cg-preview-value">
                                            <small>No {{channel.getRealName()}}</small>
                                            <strong>{{pricePreview}}</strong>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section id="cg-sincronizacao" class="cg-section">
                            <header class="cg-section-header">
                                <h2 class="cg-section-title">Sincronização de catálogo</h2>
                                <p class="cg-section-description">O que o Muven cadastra e atualiza no {{channel.getRealName()}}.</p>
                            </header>

                            <div class="cg-sync">
                                <div class="cg-sync-options" role="radiogroup" aria-label="Sincronização de catálogo">
                                    <button type="button" role="radio" class="cg-sync-option"
                                        v-for="option in allowExportListOptions" :key="option.enum"
                                        :aria-checked="String(option.enum === channelSettingsForm.allowExportWrapper.enum)"
                                        :class="{ 'is-selected': option.enum === channelSettingsForm.allowExportWrapper.enum }"
                                        @click="selectAllowExport(option)">
                                        <span class="cg-radio"></span>
                                        <span class="cg-sync-text">
                                            <span class="cg-sync-title">{{option.description}}</span>
                                            <span class="cg-sync-summary">{{getAllowExportSummary(option.enum)}}</span>
                                            <span class="cg-chip is-warning" v-if="option.enum === ChannelAllowExportEnum.All">
                                                <fa icon="exclamation-triangle" />
                                                Pode sobrescrever dados no canal
                                            </span>
                                        </span>
                                    </button>
                                </div>

                                <div class="cg-matrix" v-if="selectedAllowExportDetail">
                                    <p class="cg-matrix-caption">No canal, com a opção escolhida</p>
                                    <table class="cg-matrix-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Informação</th>
                                                <th scope="col">Cadastra</th>
                                                <th scope="col">Atualiza</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in selectedAllowExportDetail.rows" :key="row.label">
                                                <th scope="row">{{row.label}}</th>
                                                <td :class="{ 'is-yes': row.inserts }">
                                                    <fa :icon="row.inserts ? 'check' : 'minus'" />
                                                    <span class="sr-only">{{row.inserts ? 'Sim' : 'Não'}}</span>
                                                </td>
                                                <td :class="{ 'is-yes': row.updates }">
                                                    <fa :icon="row.updates ? 'check' : 'minus'" />
                                                    <span class="sr-only">{{row.updates ? 'Sim' : 'Não'}}</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>

                        <section id="cg-lojas" class="cg-section">
                            <header class="cg-section-header">
                                <h2 class="cg-section-title">Lojas</h2>
                                <p class="cg-section-description">Quais lojas da retaguarda alimentam este canal.</p>
                            </header>

                            <div class="cg-field">
                                <label>Lojas responsáveis pelo estoque</label>
                                <SelectWrapperCmp
                                    :show-remove-button="!!channelSettingsForm.stockStores.length"
                                    :disabled="!hasMoreThanOneStore"
                                    @removed="handleStockStoresRemoved">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="channelSettingsForm.stockStores"
                                                :options="storeList"
                                                :multiple="true"
                                                :show-labels="false"
                                                :disabled="!hasMoreThanOneStore"
                                                label="name"
                                                track-by="id"
                                                placeholder="" />
                                        </template>
                                </SelectWrapperCmp>
                                <small class="cg-help" v-show="channelSettingsForm.stockStores.length">Com mais de uma loja, o estoque é somado.</small>
                                <small class="cg-help" v-show="!channelSettingsForm.stockStores.length">Sem lojas escolhidas, cada loja envia o próprio estoque.</small>
                            </div>

                            <div class="cg-field">
                                <label>Loja responsável pela baixa dos pedidos</label>
                                <SelectWrapperCmp
                                    :show-remove-button="!!channelSettingsForm.orderManagementStore"
                                    :disabled="!hasMoreThanOneStore"
                                    @removed="handleOrderManagementStoreRemoved">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="channelSettingsForm.orderManagementStore"
                                                :options="storeList"
                                                :multiple="false"
                                                :show-labels="false"
                                                :disabled="!hasMoreThanOneStore"
                                                label="name"
                                                track-by="id"
                                                placeholder="" />
                                        </template>
                                </SelectWrapperCmp>
                                <small class="cg-help" v-show="!channelSettingsForm.orderManagementStore">Sem loja escolhida, cada loja recebe os próprios pedidos.</small>
                            </div>

                            <div class="cg-field">
                                <label>
                                    Loja responsável pelo catálogo
                                    <fa icon="lock" class="cg-label-lock" v-if="isCpd" />
                                </label>
                                <SelectWrapperCmp
                                    :show-remove-button="!!channelSettingsForm.priceManagementStore && !isCpd"
                                    :disabled="!hasMoreThanOneStore || isCpd"
                                    @removed="handlePriceManagementStoreRemoved">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="channelSettingsForm.priceManagementStore"
                                                :options="storeList"
                                                :multiple="false"
                                                :show-labels="false"
                                                :allow-empty="false"
                                                :disabled="!hasMoreThanOneStore || isCpd"
                                                label="name"
                                                track-by="id"
                                                placeholder="" />
                                        </template>
                                </SelectWrapperCmp>
                                <small class="cg-help" v-show="!channelSettingsForm.priceManagementStore">Sem loja escolhida, cada loja cadastra os próprios produtos.</small>

                                <p class="cg-note" v-if="isCpd">
                                    <fa icon="lock" class="cg-note-icon" />
                                    <span>Alteração restrita. Para trocar esta loja, fale com o <strong>suporte Muven</strong>.</span>
                                </p>
                                <p class="cg-note is-warning" v-else-if="hasMoreThanOneStore && isPriceManagementStoreChanged">
                                    <fa icon="exclamation-triangle" class="cg-note-icon" />
                                    <span>
                                        Ao gravar, os produtos vinculados <span v-if="initialPriceManagementStore && initialPriceManagementStore.name">à loja <strong>{{initialPriceManagementStore.name}}</strong></span>
                                        passam para <strong>{{namePriceManagementStore}}</strong>. É um processo lento e arriscado: só siga se for realmente necessário.
                                    </span>
                                </p>
                                <p class="cg-note" v-else-if="hasMoreThanOneStore">
                                    <fa icon="info-circle" class="cg-note-icon" />
                                    <span>Trocar esta loja move todos os produtos vinculados para a nova loja. Faça isso só em último caso.</span>
                                </p>
                            </div>
                        </section>

                        <section id="cg-informacoes-produto" class="cg-section" v-if="channelSettingsForm.productEntityTemplate">
                            <header class="cg-section-header">
                                <h2 class="cg-section-title">Informações do produto</h2>
                                <p class="cg-section-description">
                                    Monte o texto enviado ao canal combinando variáveis.
                                    <a href="#" class="cg-link" @click.prevent="showModalEntityTemplateExplanation()">Ver variáveis disponíveis</a>
                                </p>
                            </header>

                            <p class="cg-note is-warning cg-note-block">
                                <fa icon="exclamation-triangle" class="cg-note-icon" />
                                <span>Ao gravar mudanças aqui, <strong>todos os produtos são reprocessados e reenviados</strong> ao {{channel.getRealName()}}. Textos fora das chaves se repetem em todos os produtos e a ordem das variáveis importa.</span>
                            </p>

                            <div class="cg-template-group" v-for="group in entityTemplateFieldGroups" :key="group.label">
                                <h3 class="cg-subtitle">{{group.label}}</h3>

                                <div class="cg-field" v-for="field in group.fields" :key="field.key">
                                    <label :for="`cg-template-${field.key}`">{{field.label}}</label>
                                    <input :id="`cg-template-${field.key}`" type="text"
                                        class="form-control cg-mono"
                                        :class="{ 'is-invalid': !channelSettingsForm[field.validator]() }"
                                        v-model="channelSettingsForm.productEntityTemplate.fields[field.key]" />
                                    <small class="text-danger" v-show="!channelSettingsForm[field.validator]()">Campo inválido. Utilize pelo menos uma das variáveis disponíveis.</small>
                                </div>
                            </div>
                        </section>

                        <div class="cg-savebar" :class="{ 'is-dirty': isDirty }">
                            <p class="cg-savebar-status">
                                <span class="cg-savebar-dot"></span>
                                {{isDirty ? 'Alterações não salvas' : 'Nenhuma alteração'}}
                            </p>

                            <button type="button" class="btn btn-outline-secondary"
                                v-if="isDirty"
                                :disabled="savingContent"
                                @click="discardChanges()">
                                Descartar
                            </button>

                            <button type="button" class="btn btn-primary cg-savebar-save"
                                v-if="isDirty"
                                :disabled="savingContent"
                                @click="runChecksBeforeSaving(channelSettingsForm)">
                                <fa icon="sync-alt" spin v-if="savingContent" />
                                Gravar
                            </button>

                            <button type="button" class="btn btn-primary cg-savebar-save"
                                v-else
                                @click="backToSettingsList()">
                                <fa icon="arrow-left" />
                                Voltar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import channelService from '../services/ChannelService'
import { ChannelSettingsFormModel } from '../models/ChannelSettingsFormModel'
import { ChannelModel } from '../models/ChannelModel'
import { priceDecimals } from "@/utils/constants/PriceDecimals" 
import SelectWrapperCmp from '@/utils/components/SelectWrapperCmp'
import ConfirmByTypingModalCmp from '@/utils/components/ConfirmByTypingModalCmp'
import ChannelPageHeaderCmp from '@/channel/components/ChannelPageHeaderCmp.vue'
import { cloneDeep } from 'lodash'
import { ChannelAllowExportEnum } from '@c2b/muven-commons'
import { StoreModel } from '../../store/models/StoreModel'
import ModalService from '../../utils/services/ModalService'
import ClipboardJS from "clipboard";
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { getChannelNotFoundBreadcrumb } from '../utils/ChannelNotFoundBreadcrumb'

const ID_ENTITY_TEMPLATE_EXPLANATION_MODAL = '#muven-entity-template-explanation-modal';
const ID_MODAL_PRICE_MANAGEMENT_MODIFIED_MODAL = '#muven-price-management-store-modified-modal';
const ID_CONFIRM_ALLOW_EXPORT_ALL_MODAL = 'muven-confirm-allow-export-all-modal';

export default {
    name: 'ChannelSettingsView',
    components: {
        SelectWrapperCmp,
        ConfirmByTypingModalCmp,
        ChannelPageHeaderCmp,
    },
    data() {
        return {
            loading: true,
            savingContent: false,
            priceDecimals,
            initialPrice: 189,
            channel: new ChannelModel(),
            initialChannelStatus: false,
            breadcrumbList: [],
            channelSettingsForm: new ChannelSettingsFormModel(),
            // Foto do formulário logo após carregar: base do "Descartar" e do aviso de alterações
            initialForm: null,
            initialFormSnapshot: '',
            currentSection: 'cg-identificacao',
            sectionObserver: null,
            ChannelAllowExportEnum,
            allowExportListOptions: ChannelSettingsFormModel.getAllowExportOptions(),
            productEntityTemplateFieldOptions: ChannelSettingsFormModel.getEntityTemplateFieldOptions(),
            clipboard: null,
            initialPriceManagementStore: new StoreModel(),
            // Valor do combo antes da última troca, para poder voltar atrás
            // quando o usuário não confirma "Exportar tudo".
            previousAllowExport: {enum: ChannelAllowExportEnum.All, description: ''},
            ID_CONFIRM_ALLOW_EXPORT_ALL_MODAL,
        }
    },
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                this.channel = await channelService.getChannelById(this.$route.params.id)
                this.initialChannelStatus = this.channel.isActive;
                this.channelSettingsForm = new ChannelSettingsFormModel(this.channel)
                this.previousAllowExport = cloneDeep(this.channelSettingsForm.allowExportWrapper)

                if (!this.hasMoreThanOneStore) {
                    this.channelSettingsForm.stockStores = [
                        cloneDeep(this.storeList[0])
                    ]

                    this.channelSettingsForm.priceManagementStore = cloneDeep(this.storeList[0])
                    this.channelSettingsForm.orderManagementStore = cloneDeep(this.storeList[0])
                } else {
                    this.channelSettingsForm.stockStores = this.storeList.filter((store) => {
                        for (const storeId of this.channelSettingsForm.stockStores) {
                            if (String(storeId) === String(store.id)) {
                                return store
                            }
                        }
                    })

                    this.channelSettingsForm.priceManagementStore = this.storeList.find((s) => s.id === this.channelSettingsForm.priceManagementStore.id)
                    this.channelSettingsForm.orderManagementStore = this.storeList.find((s) => s.id === this.channelSettingsForm.orderManagementStore.id)
                }

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Canais',
                        path: '/canais'
                    },
                    {
                        id: 1,
                        description: `Configurações ${this.channel.name}`,
                        channel: this.channel,
                        path: `/canais/${this.channel.id}/configuracoes`,
                    },
                    {
                        id: 2,
                        description: `Configurações gerais`,
                        path: '',
                    },
                ]

                if (!!this.channelSettingsForm.priceManagementStore) {
                    this.initialPriceManagementStore = cloneDeep(this.channelSettingsForm.priceManagementStore)
                }
            } catch(err) {
                this.loadError = err
                this.breadcrumbList = getChannelNotFoundBreadcrumb(this.$route.params.id)
            }

            this.loading = false

            if (!this.loadError) {
                this.takeFormSnapshot()
                this.$nextTick(() => this.observeSections())
            }
        },
        takeFormSnapshot() {
            this.initialForm = cloneDeep(this.channelSettingsForm)
            this.initialFormSnapshot = JSON.stringify(this.channelSettingsForm)
        },
        discardChanges() {
            this.channelSettingsForm = cloneDeep(this.initialForm)
            this.previousAllowExport = cloneDeep(this.channelSettingsForm.allowExportWrapper)
        },
        backToSettingsList() {
            this.$router.push(`/canais/${this.channel.id}/configuracoes`)
        },
        selectAllowExport(option) {
            if (option.enum === this.channelSettingsForm.allowExportWrapper.enum) {
                return
            }

            this.channelSettingsForm.allowExportWrapper = cloneDeep(option)
            this.allowExportChangedHandler()
        },
        getAllowExportSummary(en) {
            const detail = ChannelSettingsFormModel.getAllowExportDetail(en)

            return detail ? detail.summary : ''
        },
        // Índice lateral: marca a seção que está no terço de cima da tela
        observeSections() {
            if (typeof IntersectionObserver === 'undefined') {
                return
            }

            this.sectionObserver = new IntersectionObserver((entries) => {
                entries
                    .filter((entry) => entry.isIntersecting)
                    .forEach((entry) => { this.currentSection = entry.target.id })
            }, { rootMargin: '-20% 0px -70% 0px' })

            this.sections.forEach((section) => {
                const el = document.getElementById(section.id)

                if (el) {
                    this.sectionObserver.observe(el)
                }
            })
        },
        scrollToSection(id) {
            const el = document.getElementById(id)

            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                this.currentSection = id
            }
        },
        async runChecksBeforeSaving(settingsForm) {
            // if (!settingsForm.hasPriceManagementStore()) {
            //     this.$floatingAlert.error({
            //         title: '',
            //         message: 'Não foi informada nenhuma loja responsável pelo <b>catálogo</b>. Favor escolher uma antes de continuar.'
            //     })
            //     return;
            // }

            if (!this.channel.credentialsByStore) {
                if (!settingsForm.hasOrderManagementStore()) {
                    this.$floatingAlert.error({
                        title: '',
                        message: 'Não foi informada nenhuma loja responsável pela <b>baixa dos pedidos</b>. Favor escolher uma antes de continuar.'
                    })
                    return;
                }

                if (!settingsForm.hasStockStores()) {
                    this.$floatingAlert.error({
                        title: '',
                        message: 'Não foi informada nenhuma loja responsável pelo <b>estoque</b>. Favor escolher pelo menos uma antes de continuar.'
                    })
                    return;
                }
            }

            if (!settingsForm.isPriceManagementStoreEqual(this.initialPriceManagementStore)) {
                ModalService.show(ID_MODAL_PRICE_MANAGEMENT_MODIFIED_MODAL);
                return;
            }

            await this.saveSettings(settingsForm)
        },
        /**
         * A confirmação acontece no momento da troca, e não ao gravar: o usuário
         * assume o risco antes de seguir mexendo no resto do formulário.
         *
         * Cobre os dois caminhos que levam a "Exportar tudo": o select e o botão
         * do modal de explicação de catálogo.
         */
        allowExportChangedHandler() {
            if (this.channelSettingsForm.isTurningIntoAllowExportAll(this.previousAllowExport.enum)) {
                this.$refs.confirmAllowExportAll.show()
                return
            }

            this.previousAllowExport = cloneDeep(this.channelSettingsForm.allowExportWrapper)
        },
        allowExportAllConfirmedHandler() {
            this.previousAllowExport = cloneDeep(this.channelSettingsForm.allowExportWrapper)
        },
        // Sem confirmação o valor não vale: o combo volta ao que estava antes.
        allowExportAllCancelledHandler() {
            this.channelSettingsForm.allowExportWrapper = cloneDeep(this.previousAllowExport)
        },
        async saveSettings(settingsForm) {
            this.savingContent = true;

            if (!settingsForm.isPriceManagementStoreEqual(this.initialPriceManagementStore)) {
                this.closeModalPriceManagementStoreModifiedModal();
            }

            if (!this.channel.isCustom() && !this.channel.isMagazineLuiza() && (this.initialChannelStatus === false && settingsForm.isActive === true)) {
                try {
                    const credentials = await channelService.getCredentials(this.channel.id)
                    await channelService.pingChannel(this.channel.enumChannel, credentials, this.channel.id)
                } catch (err) {
                    this.$floatingAlert.error({
                        title: '',
                        message: `
                            Não foi possível ativar a integração com o canal <b>${this.channel.name}</b>, porque o teste de conexão retornou um erro.
                            <br><br>
                            Acesse <a target="_blank" href="/canais/${this.channel.id}/configuracoes/credenciais">as credenciais</a> e faça o preenchimento correto ou mantenha o canal inativo por enquanto.
                        `,
                    })

                    this.savingContent = false;
                    return;
                }
            }

            this.$floatingAlert.hide();

            try {
                if (!settingsForm.isPriceManagementStoreEqual(this.initialPriceManagementStore)) {
                    await channelService.updatePriceManagementStore(this.channel.id, settingsForm.priceManagementStore.id);
                }

                const normalizedSettings = ChannelSettingsFormModel.normalizeBeforeSubmit(settingsForm)
                await channelService.updateSettings(this.channel.id, normalizedSettings)

                this.$floatingAlert.success({
                    message: 'Configurações gerais salvas com sucesso!',
                })                

                this.$router.push(`/canais/${this.channel.id}/configuracoes`)
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message || 'Houve um problema no momento de gravar as configurações. Tente novamente mais tarde.',
                })                
            }

            this.savingContent = false
        },
        handlePriceDecimalsPreferenceRemoved() {
            this.channelSettingsForm.priceDecimalsPreferenceString = null
        },
        handleStockStoresRemoved() {
            this.channelSettingsForm.stockStores = []
        },
        handlePriceManagementStoreRemoved() {
            this.channelSettingsForm.priceManagementStore = null
        },
        handleOrderManagementStoreRemoved() {
            this.channelSettingsForm.orderManagementStore = null
        },
        channelIsActiveModifiedHandler(info) {
            this.channelSettingsForm.isActive = info
        },
        showWithoutStockModifiedHandler(info) {
            this.channelSettingsForm.showWithoutStock = info
        },
        channelSyncCatalogModifiedHandler(info) {
            this.channelSettingsForm.syncCatalog = info
        },
        showModalEntityTemplateExplanation() {
            this.clipboard = new ClipboardJS('.entity-template-variable-item .icon-copy', {
                container: document.getElementById(ID_ENTITY_TEMPLATE_EXPLANATION_MODAL.replace('#', ''))
            })
            ModalService.show(ID_ENTITY_TEMPLATE_EXPLANATION_MODAL)
        },
        closeModalEntityTemplateExplanation() {
            ModalService.hide(ID_ENTITY_TEMPLATE_EXPLANATION_MODAL)
        },
        closeModalPriceManagementStoreModifiedModal() {
            ModalService.hide(ID_MODAL_PRICE_MANAGEMENT_MODIFIED_MODAL)
        },
    },
    mixins: [
        ResourceLoadError,
    ],
    mounted() {
        this.load()
    },
    beforeDestroy() {
        if (this.sectionObserver) {
            this.sectionObserver.disconnect()
        }
    },
    computed: {
        ...mapState({
            storeList: (state) => {
                return StoreModel.getStoreListWithIdAndName(state.storeList)
            },
            subscriber: (state) => state.subscriber,
        }),
        ...mapGetters([
            'hasMoreThanOneStore',
        ]),
        sections() {
            const sections = [
                { id: 'cg-identificacao', label: 'Identificação' },
                { id: 'cg-estoque-preco', label: 'Estoque e preço' },
                { id: 'cg-sincronizacao', label: 'Sincronização' },
                { id: 'cg-lojas', label: 'Lojas' },
            ]

            if (this.channelSettingsForm.productEntityTemplate) {
                sections.push({ id: 'cg-informacoes-produto', label: 'Informações do produto' })
            }

            return sections
        },
        entityTemplateFieldGroups() {
            return [
                {
                    label: 'Conteúdo',
                    fields: [
                        { key: 'name', label: 'Nome', validator: 'isEntityTemplateFieldNameValid' },
                        { key: 'description', label: 'Descrição principal', validator: 'isEntityTemplateFieldDescriptionValid' },
                        { key: 'descriptionLong', label: 'Descrição longa', validator: 'isEntityTemplateFieldDescriptionLongValid' },
                        { key: 'descriptionShort', label: 'Descrição curta', validator: 'isEntityTemplateFieldDescriptionShortValid' },
                    ],
                },
                {
                    label: 'SEO',
                    fields: [
                        { key: 'seoTitle', label: 'Título', validator: 'isEntityTemplateFieldSeoTitleValid' },
                        { key: 'seoDescription', label: 'Descrição', validator: 'isEntityTemplateFieldSeoDescriptionValid' },
                        { key: 'seoKeywords', label: 'Palavras-chave', validator: 'isEntityTemplateFieldSeoKeywordsValid' },
                    ],
                },
            ]
        },
        isDirty() {
            return !!this.initialFormSnapshot && JSON.stringify(this.channelSettingsForm) !== this.initialFormSnapshot
        },
        activationHint() {
            if (this.channelSettingsForm.isActive && !this.initialChannelStatus) {
                return 'Ao gravar, testamos a conexão antes de ativar.'
            }

            return this.channelSettingsForm.isActive
                ? 'Pedidos, estoque e preços sincronizam com o canal.'
                : 'Nada é enviado ou recebido deste canal.'
        },
        selectedAllowExportDetail() {
            return ChannelSettingsFormModel.getAllowExportDetail(this.channelSettingsForm.allowExportWrapper.enum)
        },
        isPriceManagementStoreChanged() {
            return !this.channelSettingsForm.isPriceManagementStoreEqual(this.initialPriceManagementStore)
        },
        stockPreview() {
            const pct = Math.min(Math.max(Number(this.channelSettingsForm.availableStockPercentage) || 0, 0), 100)

            return Math.floor(pct)
        },
        // Sem preferência de centavos, mostra os centavos que o cálculo de fato dá
        // (o `finalPriceExample` deixa "XX" nesse caso)
        pricePreview() {
            if (this.channelSettingsForm.priceDecimalsPreferenceString) {
                return this.finalPriceExample
            }

            const value = this.initialPrice * (Number(this.channelSettingsForm.pricePercentage) || 0) / 100

            return `R$ ${value.toFixed(2).replace('.', ',')}`
        },
        priceDeltaLabel() {
            const delta = Math.round((Number(this.channelSettingsForm.pricePercentage) || 100) - 100)

            if (delta === 0) {
                return 'mesmo preço'
            }

            return `${delta > 0 ? '+' : ''}${delta}%`
        },
        overwrittenByAllowExportAll() {
            return ['produtos', 'estoque', 'preços', 'marcas', 'categorias', 'imagens']
        },
        isCpd() {
            return this.subscriber && this.subscriber.publicKey === '53bbb2787224517caa6f634c573c6fb1'
        },
        namePriceManagementStore() {
            return this.channelSettingsForm.priceManagementStore ? this.channelSettingsForm.priceManagementStore.name : ''
        },
        finalPriceExample() {
            let decimals = 'XX'

            if (!!this.channelSettingsForm.priceDecimalsPreferenceString) {
                decimals = this.channelSettingsForm.priceDecimalsPreferenceString                
            }

            let intResult = parseInt(this.initialPrice * (this.channelSettingsForm.pricePercentage/100), 10)

            if (decimals === '100') {
                intResult = intResult + 1
                decimals = '00'
            }

            return `R$ ${intResult},${decimals}`
        },
    },
}
</script>

<style lang="scss" scoped>
.modal {
    .muven-tip {
        &:hover {
            border-left-color: var(--info-border);
            background-color: var(--surface-2);
        }

        .entity-template-variable-item {
            .icon-copy {
                position: absolute;
                top: 10px;
                right: 10px;
            }
        }
    }
}

.confirm-allow-export-label {
    margin-top: 18px;
    margin-bottom: 6px;
    font-size: .75em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-faint);
}

.confirm-allow-export-chips {
    margin-bottom: 0;

    span {
        display: inline-block;
        margin: 0 5px 5px 0;
        padding: 3px 9px;
        border: 1px solid var(--danger-border);
        border-radius: 11px;
        background-color: var(--danger-bg);
        color: var(--danger-fg);
        font-size: .85em;
        font-weight: 600;
    }
}

.confirm-allow-export-danger {
    margin-top: 16px;
    margin-bottom: 0;
    padding-left: 12px;
    border-left: 3px solid var(--danger-solid);
    color: var(--danger-fg);
    line-height: 1.5;
}


/* ══ Configurações gerais ══
   Mesma regra da lista de configurações: neutro por padrão. A marca do canal
   só no cabeçalho, o roxo do Muven só em foco/seleção, amarelo só em aviso. */

$ease-out: cubic-bezier(.22, 1, .36, 1);
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;

.channel-general {
    max-width: 1180px;
    margin: 0 auto;
    animation: cg-rise .5s $ease-out both;
}

/* ── Ativação (dentro do cabeçalho) ── */

.cg-activation {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 10px 10px 16px;
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    background: var(--surface-2);
    transition: border-color .2s, background-color .2s;

    &.is-on {
        border-color: var(--success-border);
        background: var(--success-bg);

        .cg-activation-title {
            color: var(--success-fg);
        }
    }
}

.cg-activation-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--text-strong);
    font-weight: 700;
}

.cg-activation-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

.cg-activation-hint {
    max-width: 240px;
    margin: 2px 0 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.35;
}

// A raiz do SwitchToggle recebe esta classe; tira a margem vertical dele
// (o label dele é absoluto: sem largura própria, o texto passa por baixo)
.cg-activation-switch.muven-switch__container {
    flex-shrink: 0;
    width: 47px;
    min-height: 27px;
    margin: 0;
}

/* ── Layout: índice + seções ── */

.cg-layout {
    display: grid;
    grid-template-columns: 190px minmax(0, 1fr);
    align-items: start;
    gap: 28px;
    margin-top: 20px;
}

.cg-index {
    position: sticky;
    top: 76px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 2px;
    border-left: 1px solid var(--border-subtle);
}

.cg-index-link {
    position: relative;
    margin-left: -3px;
    padding: 7px 12px;
    border-left: 2px solid transparent;
    color: var(--text-muted);
    font-size: 14px;
    font-weight: 600;
    transition: color .15s, border-color .15s;

    &:hover {
        color: var(--text-strong);
        text-decoration: none;
    }

    &.is-current {
        border-left-color: var(--brand);
        color: var(--brand);
    }
}

.cg-sections {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;

    &[aria-busy="true"] .cg-section {
        opacity: .6;
        pointer-events: none;
    }
}

.cg-section {
    padding: 24px 26px 26px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
    // Âncora do índice não pode parar embaixo do menu do topo
    scroll-margin-top: 80px;
    transition: opacity .2s;
}

.cg-section-header {
    margin-bottom: 20px;
}

.cg-section-title {
    margin: 0;
    color: var(--text-strong);
    font-family: $display;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -.4px;
}

.cg-section-description {
    margin: 4px 0 0;
    color: var(--text-muted);
    font-size: 14px;
}

.cg-subtitle {
    margin: 22px 0 12px;
    color: var(--text-faint);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.cg-link {
    margin-left: 4px;
    color: var(--brand);
    font-weight: 600;
}

.cg-divider {
    margin: 22px 0;
    border-top: 1px solid var(--border-subtle);
}

/* ── Campos ── */

.cg-field {
    min-width: 0;

    & + & {
        margin-top: 18px;
    }

    label {
        margin-bottom: 6px;
        color: var(--text-strong);
        font-weight: 600;
    }
}

.cg-help {
    display: block;
    margin-top: 6px;
    color: var(--text-muted);
    font-size: 13px;
}

.cg-label-lock {
    margin-left: 6px;
    color: var(--text-faint);
    font-size: .85em;
}

.cg-input-suffix {
    position: relative;

    .form-control {
        padding-right: 36px;
        font-variant-numeric: tabular-nums;
    }
}

.cg-suffix {
    position: absolute;
    top: 50%;
    right: 14px;
    color: var(--text-faint);
    font-weight: 600;
    transform: translateY(-50%);
    pointer-events: none;
}

.cg-mono {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 13px;
}

/* Campo à esquerda, prévia à direita */
.cg-split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 300px);
    align-items: start;
    gap: 24px;
}

.cg-field-pair {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 14px;

    .cg-field + .cg-field {
        margin-top: 0;
    }
}

.cg-field-pair-help {
    grid-column: 1 / -1;
}

/* ── Prévia ao vivo ── */

.cg-preview {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px dashed var(--border);
    border-radius: 12px;
    background: var(--surface-2);
}

.cg-preview-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-faint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.cg-delta {
    padding: 2px 8px;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface-1);
    color: var(--text-soft);
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
    font-variant-numeric: tabular-nums;
}

.cg-preview-flow {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
}

.cg-preview-value {
    display: flex;
    flex-direction: column;
    min-width: 0;

    small {
        overflow: hidden;
        color: var(--text-muted);
        font-size: 12px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        color: var(--text-strong);
        font-family: $display;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: -.3px;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    &:last-child {
        align-items: flex-end;
        text-align: right;
    }
}

.cg-preview-arrow {
    flex-shrink: 0;
    margin-bottom: 7px;
    color: var(--text-faint);
}

.cg-meter {
    height: 6px;
    overflow: hidden;
    border-radius: 3px;
    background: var(--surface-3);
}

.cg-meter-fill {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--text-soft);
    transition: width .35s $ease-out;
}

/* ── Sincronização ── */

.cg-sync {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 300px);
    align-items: start;
    gap: 24px;
}

.cg-sync-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cg-sync-option {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-1);
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color .15s, background-color .15s, box-shadow .15s;

    &:hover {
        border-color: var(--border-strong);
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px var(--focus-ring);
    }

    &.is-selected {
        border-color: var(--brand-soft);
        background: var(--tint-brand-soft);

        .cg-radio {
            border-color: var(--brand);

            &::after {
                transform: scale(1);
            }
        }
    }
}

.cg-radio {
    position: relative;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    border: 2px solid var(--border-strong);
    border-radius: 50%;
    transition: border-color .15s;

    &::after {
        content: '';
        position: absolute;
        top: 3px;
        right: 3px;
        bottom: 3px;
        left: 3px;
        border-radius: 50%;
        background: var(--brand);
        transform: scale(0);
        transition: transform .2s $ease-out;
    }
}

.cg-sync-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
}

.cg-sync-title {
    color: var(--text-strong);
    font-weight: 700;
}

.cg-sync-summary {
    color: var(--text-muted);
    font-size: 13px;
}

.cg-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 2px 9px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;

    &.is-warning {
        border-color: var(--warning-border);
        background: var(--warning-bg);
        color: var(--warning-fg);
    }
}

.cg-matrix {
    position: sticky;
    top: 80px;
    padding: 14px 16px;
    border: 1px dashed var(--border);
    border-radius: 12px;
    background: var(--surface-2);
}

.cg-matrix-caption {
    margin: 0 0 8px;
    color: var(--text-faint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.cg-matrix-table {
    width: 100%;
    font-size: 13px;

    th,
    td {
        padding: 5px 0;
        border-bottom: 1px solid var(--border-subtle);
    }

    tbody tr:last-child {
        th,
        td {
            border-bottom: none;
        }
    }

    thead th {
        color: var(--text-muted);
        font-size: 12px;
        font-weight: 700;
    }

    thead th + th,
    td {
        width: 64px;
        text-align: center;
    }

    tbody th {
        color: var(--text-soft);
        font-weight: 600;
    }

    td {
        color: var(--text-disabled);

        &.is-yes {
            color: var(--text-strong);
        }
    }
}

/* ── Notas e avisos ── */

.cg-note {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 10px 0 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.45;

    &.is-warning {
        padding: 10px 12px;
        border: 1px solid var(--warning-border);
        border-radius: 10px;
        background: var(--warning-bg);
        color: var(--warning-fg);
    }
}

.cg-note-block {
    margin: 0 0 4px;
}

.cg-note-icon {
    flex-shrink: 0;
    margin-top: 3px;
}

/* ── Barra de gravar ── */

.cg-savebar {
    position: sticky;
    bottom: 12px;
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px 10px 18px;
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    background: color-mix(in srgb, var(--surface-1) 92%, transparent);
    box-shadow: var(--shadow-md);
    backdrop-filter: blur(8px);
    transition: border-color .2s;

    &.is-dirty {
        border-color: var(--warning-border);

        .cg-savebar-status {
            color: var(--warning-fg);
        }

        .cg-savebar-dot {
            background: var(--warning-solid);
        }
    }
}

.cg-savebar-status {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--text-faint);
    font-weight: 600;
}

.cg-savebar-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--border-strong);
}

.cg-savebar-save {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 110px;

    // O override global do btn-primary não cobre o desabilitado (gravando):
    // sem isto ele volta ao azul do Bootstrap.
    &:disabled {
        border-color: var(--brand-solid);
        background-color: var(--brand-solid);
        opacity: .65;
    }
}

@keyframes cg-rise {
    from { opacity: 0; translate: 0 10px; }
    to   { opacity: 1; translate: 0 0; }
}

/* ── Responsivo ── */

@media (max-width: 1200px) {
    .cg-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .cg-index {
        display: none;
    }
}

@media (max-width: 860px) {
    .cg-split,
    .cg-sync {
        grid-template-columns: minmax(0, 1fr);
    }

    .cg-matrix {
        position: static;
    }
}

@media (max-width: 576px) {
    .cg-section {
        padding: 20px 16px;
    }

    .cg-activation {
        width: 100%;
    }

    .cg-activation-hint {
        max-width: none;
    }

    .cg-field-pair {
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .channel-general {
        animation: none;
    }

    .cg-meter-fill,
    .cg-radio::after {
        transition: none;
    }
}
</style>
